import { Platform } from "react-native";
import { Middleware, UnknownAction } from "redux";
import { AuthActions } from "./Login/State";
import * as Sentry from "@sentry/react-native";

const CHANNEL_NAME = "auth-sync-channel";
const WHITELIST = [
  AuthActions.AuthSuccess,
  AuthActions.FinishedSetup,
  AuthActions.RefreshAuthSuccess,
];

export function reduxBroadcastMiddleware(): Middleware {
  // When BroadcastChannel is not available, return opaque middleware
  if (Platform.OS !== "web" || !window.BroadcastChannel) {
    return (store) => (next) => (action) => next(action);
  }

  const broadcastChannel = new BroadcastChannel(CHANNEL_NAME);

  // Note: using methods available in browsers which support BroadcastChannel
  return (store) => {
    // Listener
    broadcastChannel.addEventListener("message", (messageEvent) => {
      Sentry.addBreadcrumb({
        message: "AUTH_SYNC_MESSAGE",
        data: {
          type: messageEvent?.data?.type,
        },
      });
      store.dispatch({
        ...messageEvent.data,
        isBroadcastReceive: true,
      });
    });

    // Not adding `messageerror` listener to handle data which cannot be deserialized
    // as such should be catched at serialization

    // Emitter
    return (next) => (action) => {
      // When action is a thunk, wait until it's resolved
      if (action instanceof Function) {
        return next(action);
      }

      const { isBroadcastReceive, ...newAction } = action as UnknownAction;

      // Don't repost if it's an action received from channel
      if (
        !isBroadcastReceive &&
        (WHITELIST as string[]).includes((action as UnknownAction).type)
      ) {
        try {
          /** @throws {DOMException} - Cannot serialize (DataCloneError) */
          broadcastChannel.postMessage(action);
        } catch (error) {
          console.error(
            "reduxStateChannelMiddleware post message error:",
            error,
            action
          );
        }
      }

      return next(newAction);
    };
  };
}
